/**
 * /marketplace — discover and install knode-os capability packs.
 *
 * Two tabs: datapacks (curated KBs) and agent templates (ready-made configs).
 * See `docs/adr/0014-marketplace-scope.md` for the scope rationale.
 *
 * The loader fetches from the real backend API. If the API is unreachable
 * (e.g. gateway not running), we fall back to the static mock so the page
 * still renders in offline/dev scenarios.
 */

import { useLoaderData } from "react-router-dom";

import { fetchCatalog, fetchInstalledIds } from "@/features/marketplace/marketplace-api";
import { MarketplaceBrowser } from "@/features/marketplace/marketplace-browser";
import { MOCK_CATALOG, readInstalledIds } from "@/features/marketplace/marketplace-catalog-mock";
import { MarketplacePageHeader } from "@/features/marketplace/marketplace-page-header";
import type { MarketplaceItem } from "@/lib/types";

interface MarketplaceLoaderData {
  catalog: MarketplaceItem[];
  installedIds: string[];
}

export async function marketplaceLoader(): Promise<MarketplaceLoaderData> {
  try {
    const [catalog, installedIds] = await Promise.all([fetchCatalog(), fetchInstalledIds()]);
    return { catalog, installedIds };
  } catch {
    // Fallback to mock if API is unavailable (dev without gateway, etc.)
    return {
      catalog: MOCK_CATALOG,
      installedIds: readInstalledIds(),
    };
  }
}

export default function MarketplaceRoute() {
  const { catalog, installedIds } = useLoaderData() as MarketplaceLoaderData;
  return (
    <section className="space-y-4">
      <MarketplacePageHeader />
      <MarketplaceBrowser initialCatalog={catalog} initialInstalledIds={installedIds} />
    </section>
  );
}
